import { type PlannerWorkflow } from "./types";

type StreamEvent = {
  type: string;
  payload?: any;
  at: number;
};

type DesignPhaseState = {
  isStreaming: boolean;
  isDesignTimeExecuting: boolean;
  isWaitingForInput: boolean;
  /**
   * Mirrors `useChatStore`'s `workflowRunStatus` exactly. The narrower
   * three-value union this used to declare did not match the store, and the
   * checks below only ever compare against "running" and "completed" anyway.
   */
  workflowRunStatus:
    | "waiting"
    | "running"
    | "completed"
    | "failed"
    | "cancelled"
    | "queued"
    | "timeout"
    | null;
  currentThinking: string | null;
  lastStreamEvent: StreamEvent | null;
  plannedWorkflow: PlannerWorkflow | null;
  currentSessionWorkflowId?: string | bigint | null; // workflowId from the current session/chat
};

/**
 * Determines if navigation should be blocked based on the current design phase state.
 * Returns blocking status and a dynamic message describing what's happening.
 */
export function getDesignPhaseBlockingState(state: DesignPhaseState) {
  const {
    isStreaming,
    isDesignTimeExecuting,
    isWaitingForInput,
    workflowRunStatus,
    currentThinking,
    lastStreamEvent,
    plannedWorkflow,
    currentSessionWorkflowId,
  } = state;

  // Check if workflow has been saved
  // A workflow is considered saved if:
  // 1. Current session has a workflowId (from sessions store), OR
  // 2. plannedWorkflow has a workflowId property, OR
  // 3. We received a "Agentic workflow saved" notification with workflowId
  const workflowSaved = (() => {
    // Primary check: current session has workflowId (most reliable)
    if (currentSessionWorkflowId != null) {
      return true;
    }
    // Check if plannedWorkflow has workflowId
    if (
      plannedWorkflow &&
      (plannedWorkflow as any).workflowId &&
      typeof (plannedWorkflow as any).workflowId === "string"
    ) {
      return true;
    }
    // Check if last notification was "Agentic workflow saved" with workflowId
    if (lastStreamEvent?.type === "notification" && lastStreamEvent.payload) {
      const notificationType = lastStreamEvent.payload.type?.toLowerCase();
      const content = String(
        lastStreamEvent.payload.content || "",
      ).toLowerCase();
      const hasWorkflowId = Boolean(
        lastStreamEvent.payload.workflowId ||
        lastStreamEvent.payload.workflow_id,
      );
      const status = lastStreamEvent.payload.status?.toLowerCase();

      // Check for "agent_saved" type or content match
      if (
        (notificationType === "agent_saved" ||
          content.includes("agentic workflow saved") ||
          content.includes("workflow saved")) &&
        hasWorkflowId &&
        (status === "completed" || hasWorkflowId)
      ) {
        return true;
      }
    }
    return false;
  })();

  // Extract notification content and status from currentThinking or lastStreamEvent
  const getNotificationInfo = () => {
    if (currentThinking) {
      const content = currentThinking.toLowerCase();
      // Check lastStreamEvent for status
      if (lastStreamEvent?.type === "notification" && lastStreamEvent.payload) {
        const status =
          typeof lastStreamEvent.payload.status === "string"
            ? lastStreamEvent.payload.status.toLowerCase()
            : null;
        return { content, status };
      }
      return { content, status: null };
    }
    return null;
  };

  const notificationInfo = getNotificationInfo();

  // BLOCK: Active streaming (any SSE activity)
  if (isStreaming) {
    if (notificationInfo) {
      const { content, status } = notificationInfo;

      // Planning phase
      if (
        content.includes("planning agentic workflow") &&
        status === "started"
      ) {
        return {
          shouldBlock: true,
          message:
            "The system is currently planning your workgraph. If you navigate away, the planning process will be interrupted and you'll need to start over.",
          title: "Planning in Progress",
        };
      }

      // Validating phase
      if (
        content.includes("validating workflow plan") &&
        status === "started"
      ) {
        return {
          shouldBlock: true,
          message:
            "The workgraph plan is being validated. Please wait for validation to complete before navigating away.",
          title: "Validating Workgraph",
        };
      }

      // Preparing execution
      if (
        content.includes("preparing agentic workflow") &&
        status === "started"
      ) {
        return {
          shouldBlock: true,
          message:
            "The workflow is being prepared for execution. Navigation will interrupt this process.",
          title: "Preparing Execution",
        };
      }

      // Starting execution
      if (
        content.includes("starting agentic workflow") &&
        status === "started"
      ) {
        return {
          shouldBlock: true,
          message:
            "The workgraph execution is starting. Please wait for it to begin before navigating away.",
          title: "Starting Execution",
        };
      }

      // Saving workflow
      if (content.includes("saving workflow") && status === "started") {
        return {
          shouldBlock: true,
          message:
            "Your workflow is being saved. Please wait for the save to complete before navigating away.",
          title: "Saving Workflow",
        };
      }

      // Generic streaming message
      return {
        shouldBlock: true,
        message:
          "An operation is in progress. If you navigate away, the current process will be interrupted.",
        title: "Operation in Progress",
      };
    }

    // Generic streaming without specific notification
    return {
      shouldBlock: true,
      message:
        "A process is currently running. Navigation will interrupt the current operation.",
      title: "Process Running",
    };
  }

  // BLOCK: Workflow being created/updated
  if (lastStreamEvent?.type === "workflow") {
    return {
      shouldBlock: true,
      message:
        "A workgraph is being created or updated. Please wait for this to complete before navigating away.",
      title: "Workgraph Creation in Progress",
    };
  }

  // BLOCK: Waiting for user input (interrupt state)
  if (isWaitingForInput || lastStreamEvent?.type === "interrupt") {
    return {
      shouldBlock: true,
      message:
        "The workflow is waiting for your input. Please provide the required information before navigating away.",
      title: "Input Required",
    };
  }

  // BLOCK: Workflow execution running
  if (
    workflowRunStatus === "running" ||
    isDesignTimeExecuting ||
    lastStreamEvent?.type === "workflow-run"
  ) {
    const runStatus =
      lastStreamEvent?.payload?.status ||
      (workflowRunStatus === "running" ? "running" : null);

    if (runStatus === "running") {
      return {
        shouldBlock: true,
        message:
          "The workgraph is currently executing. If you navigate away, the execution will continue but you'll lose visibility into the progress.",
        title: "Workgraph Executing",
      };
    }
  }

  // BLOCK: Activity/step running
  if (lastStreamEvent?.type === "activity-run") {
    const activityStatus = lastStreamEvent?.payload?.status;
    if (activityStatus === "running") {
      const stepName =
        lastStreamEvent?.payload?.stepId ||
        lastStreamEvent?.payload?.step_name ||
        "a step";
      return {
        shouldBlock: true,
        message: `Workflow step "${stepName}" is currently running. Please wait for it to complete before navigating away.`,
        title: "Step Execution in Progress",
      };
    }
  }

  // BLOCK: Resume event (execution continuing)
  if (lastStreamEvent?.type === "resume") {
    return {
      shouldBlock: true,
      message:
        "The workgraph execution is resuming. Please wait for it to continue before navigating away.",
      title: "Resuming Execution",
    };
  }

  // ALLOW: Workflow saved and execution complete
  if (lastStreamEvent?.type === "notification" && lastStreamEvent.payload) {
    const content = String(lastStreamEvent.payload.content || "").toLowerCase();
    const notificationType = lastStreamEvent.payload.type?.toLowerCase();
    const status = lastStreamEvent.payload.status?.toLowerCase();
    const hasWorkflowId = Boolean(
      lastStreamEvent.payload.workflowId || lastStreamEvent.payload.workflow_id,
    );

    // Workflow saved (check for "agent_saved" type or content match)
    if (
      (notificationType === "agent_saved" ||
        content.includes("agentic workflow saved") ||
        content.includes("workflow saved")) &&
      (status === "completed" || hasWorkflowId)
    ) {
      return {
        shouldBlock: false,
        message: null,
        title: null,
      };
    }

    // Execution complete
    if (
      (content.includes("execution complete") ||
        content.includes("agentic workflow finished")) &&
      status === "completed"
    ) {
      // Only allow if workflow is also saved
      if (workflowSaved || hasWorkflowId) {
        return {
          shouldBlock: false,
          message: null,
          title: null,
        };
      }
    }
  }

  // ALLOW: Workflow run completed and saved
  if (
    workflowRunStatus === "completed" &&
    !isStreaming &&
    !isDesignTimeExecuting &&
    !isWaitingForInput &&
    workflowSaved
  ) {
    return {
      shouldBlock: false,
      message: null,
      title: null,
    };
  }

  // DEFAULT: Allow navigation if no blocking conditions
  // We only block based on active operations or specific notification-based phases.
  // We do NOT block just because a workflow exists - blocking is based on:
  // 1. Active operations (streaming, executing, waiting for input, etc.)
  // 2. Specific notification/content indicating a blocking phase (planning, validation, etc.)
  // Once a workflow plan is received and there's no active operation, navigation is allowed.
  return {
    shouldBlock: false,
    message: null,
    title: null,
  };
}
